import { keepPreviousData, useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { appliesQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";

export type ApplyFilterOption = { label: string; value: string; count: number };

/**
 * The drawer's dimensions, each a plain list of options.
 *
 * `client` and `integration` arrive complete — they are bounded by their catalogues and the drawer filters
 * them in the browser. The three identifier dimensions arrive as a first page only and are paged on scroll
 * through {@link useApplyFilterOptions}: each holds roughly one distinct value per apply, so a default
 * date range would otherwise yield tens of thousands of options.
 *
 * These stay arrays rather than becoming paged `{ items, hasMore }` objects on purpose. The API deploys ahead
 * of the frontend, so a bundle already running in a browser would meet the new response first — a shorter
 * array it survives, an object it would not.
 */
export type ApplyFiltersData = {
  client: ApplyFilterOption[];
  integration: ApplyFilterOption[];
  applicationSourceType: ApplyFilterOption[];
  matchId: ApplyFilterOption[];
  candidateId: ApplyFilterOption[];
  publicationId: ApplyFilterOption[];
};

export type UseApplyFiltersParams = {
  clients?: string[];
  integrations?: string[];
  applicationSourceTypes?: string[];
  matchIds?: string[];
  candidateIds?: string[];
  publicationIds?: string[];
  search?: string;
  tenantId?: string | null;
  startDate?: string;
  endDate?: string;
};

export function useApplyFilters(
  params: UseApplyFiltersParams
): UseQueryResult<ApplyFiltersData, AxiosError> {
  return useQuery({
    queryKey: appliesQueryKeys.filterCounts(params),
    queryFn: () =>
      instance
        .get<ApplyFiltersData>("/api/v2/Applies/FilterCounts", { params })
        .then((res) => res.data),
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
